import errorMessages from '@common/constants/errorMessages';
import {
  isAllDigits,
  isEmpty,
  notEmpty,
  validateEmployerIdentificationNumber,
  validateFullSocialSecurityNumber,
  validatePhoneNumber,
  validateZipCode,
} from '@common/paymentsUtils/inputValidator';

const BIRTHDAY_MIN = 1;
const BIRTHDAY_MAX = 31;

function isRequiredFieldEmpty(value, errorMessage) {
  if (isEmpty(value)) {
    return errorMessage;
  }
  return errorMessages.CLEAR_ERROR;
}

function isZipCodeValid(zipCode) {
  if (isEmpty(zipCode)) {
    return errorMessages.ZIP_CODE_EMPTY;
  } else if (zipCode.length !== 5) {
    return errorMessages.ZIP_CODE_INVALID;
  } else if (!validateZipCode(zipCode)) {
    return errorMessages.ZIP_CODE_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isPhoneNumberValid(phoneNumber) {
  if (!validatePhoneNumber(phoneNumber)) {
    return errorMessages.PHONE_NUMBER_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isDayOfBirthValid(dayOfBirth) {
  if (isEmpty(dayOfBirth)) {
    return errorMessages.DAY_OF_BIRTH_EMPTY;
  } else if (!isAllDigits(dayOfBirth)) {
    return errorMessages.DAY_OF_BIRTH_INVALID;
  } else if (dayOfBirth < BIRTHDAY_MIN) {
    return errorMessages.DAY_OF_BIRTH_INVALID;
  } else if (dayOfBirth > BIRTHDAY_MAX) {
    return errorMessages.DAY_OF_BIRTH_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isYearOfBirthValid(yearOfBirth, minYear, maxYear) {
  if (isEmpty(yearOfBirth)) {
    return errorMessages.YEAR_OF_BIRTH_EMPTY;
  } else if (!isAllDigits(yearOfBirth)) {
    return errorMessages.YEAR_OF_BIRTH_INVALID;
  } else if (yearOfBirth < minYear) {
    return errorMessages.YEAR_OF_BIRTH_INVALID;
  } else if (yearOfBirth > maxYear) {
    return errorMessages.BIRTH_DATE_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isFullSocialSecurityNumberValid(fullSocialSecurityNumber) {
  if (!validateFullSocialSecurityNumber(fullSocialSecurityNumber)) {
    return errorMessages.FULL_SSN_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

// To validate if user is 18+
function isDateOfBirthValid(monthOfBirth, dayOfBirth, yearOfBirth, maxBirthDate) {
  const maxMonth = maxBirthDate.getUTCMonth() + 1;
  const maxDay = maxBirthDate.getUTCDate();
  const maxYear = maxBirthDate.getUTCFullYear();

  if (notEmpty(yearOfBirth) && notEmpty(dayOfBirth) && notEmpty(monthOfBirth)) {
    if (Number(yearOfBirth) === maxYear) {
      if (monthOfBirth > maxMonth) {
        return errorMessages.BIRTH_DATE_INVALID;
      } else if (Number(monthOfBirth) === maxMonth) {
        if (dayOfBirth > maxDay) {
          return errorMessages.BIRTH_DATE_INVALID;
        }
      }
    }
  }
  return errorMessages.CLEAR_ERROR;
}

function isEmployerIdentificationNumberValid(employerIdentificationNumber) {
  if (!validateEmployerIdentificationNumber(employerIdentificationNumber)) {
    return errorMessages.EMPLOYER_IDENTIFICATION_NUMBER_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}
export function validateAllInputs(ctx) {
  const {
    firstName,
    lastName,
    street,
    city,
    state,
    zipCode,
    phoneNumber,
    monthOfBirth,
    dayOfBirth,
    yearOfBirth,
    fullSocialSecurityNumber,
    businessName,
    employerIdentificationNumber,
    businessStreet,
    businessCity,
    businessState,
    businessZipCode,
  } = ctx.state;

  const { landlordBirthDayMax, landlordBirthDayMin, selectedOption } = ctx.props;

  const maxBirthDate = new Date(landlordBirthDayMax);
  const minBirthDate = new Date(landlordBirthDayMin);

  const errors: Record<string, any>[] = [];
  const firstNameError = isRequiredFieldEmpty(firstName.value, errorMessages.LEGAL_FIRST_NAME_EMPTY);
  const lastNameError = isRequiredFieldEmpty(lastName.value, errorMessages.LAST_NAME_EMPTY);
  const streetError = isRequiredFieldEmpty(street.value, errorMessages.STREET_EMPTY);
  const cityError = isRequiredFieldEmpty(city.value, errorMessages.CITY_EMPTY);
  const stateError = isRequiredFieldEmpty(state.value, errorMessages.STATE_EMPTY);
  const zipCodeError = isZipCodeValid(zipCode.value);
  const phoneNumberError = isPhoneNumberValid(phoneNumber.value);
  const monthOfBirthError = isRequiredFieldEmpty(monthOfBirth.value, errorMessages.MONTH_OF_BIRTH_EMPTY);
  const dayOfBirthError = isDayOfBirthValid(dayOfBirth.value);
  const yearOfBirthError = isYearOfBirthValid(
    yearOfBirth.value,
    minBirthDate.getUTCFullYear(),
    maxBirthDate.getUTCFullYear(),
  );
  const fullSocialSecurityNumberError = isFullSocialSecurityNumberValid(fullSocialSecurityNumber.value);
  const dateOfBirthError = isDateOfBirthValid(monthOfBirth.value, dayOfBirth.value, yearOfBirth.value, maxBirthDate);
  const yearOfBirthCommonError = yearOfBirthError || dateOfBirthError || '';

  if (firstNameError) {
    errors.push({ firstName: firstNameError });
  }

  if (lastNameError) {
    errors.push({ lastName: lastNameError });
  }

  if (streetError) {
    errors.push({ street: streetError });
  }

  if (cityError) {
    errors.push({ city: cityError });
  }

  if (stateError) {
    errors.push({ state: stateError });
  }

  if (zipCodeError) {
    errors.push({ zipCode: zipCodeError });
  }

  if (phoneNumberError) {
    errors.push({ phoneNumber: phoneNumberError });
  }

  if (monthOfBirthError) {
    errors.push({ monthOfBirth: monthOfBirthError });
  }

  if (dayOfBirthError) {
    errors.push({ dayOfBirth: dayOfBirthError });
  }

  if (yearOfBirthCommonError) {
    errors.push({ yearOfBirth: yearOfBirthCommonError });
  }

  if (fullSocialSecurityNumberError) {
    errors.push({ fullSocialSecurityNumber: fullSocialSecurityNumberError });
  }

  let businessFields = {};

  if (selectedOption === 'single_member_llc' || selectedOption === 'multi_member_llc') {
    const businessNameError = isRequiredFieldEmpty(businessName.value, errorMessages.BUSINESS_NAME_EMPTY);
    if (businessNameError) {
      errors.push({ businessName: businessNameError });
    }
    const employerIdentificationNumberError = isEmployerIdentificationNumberValid(employerIdentificationNumber.value);
    if (employerIdentificationNumberError) {
      errors.push({ employerIdentificationNumber: employerIdentificationNumberError });
    }

    businessFields = {
      businessName: { ...businessName, error: businessNameError },
      employerIdentificationNumber: { ...employerIdentificationNumber, error: employerIdentificationNumberError },
    };

    if (selectedOption === 'multi_member_llc') {
      const businessStreetError = isRequiredFieldEmpty(businessStreet.value, errorMessages.BUSINESS_STREET_EMPTY);
      if (businessStreetError) {
        errors.push({ businessStreet: businessStreetError });
      }
      const businessCityError = isRequiredFieldEmpty(businessCity.value, errorMessages.BUSINESS_CITY_EMPTY);
      if (businessCityError) {
        errors.push({ businessCity: businessCityError });
      }
      const businessStateError = isRequiredFieldEmpty(businessState.value, errorMessages.BUSINESS_STATE_EMPTY);
      if (businessStateError) {
        errors.push({ businessState: businessStateError });
      }
      const businessZipCodeError = isZipCodeValid(businessZipCode.value);
      if (businessZipCodeError) {
        errors.push({ businessZipCode: businessZipCodeError });
      }
      businessFields = {
        ...businessFields,
        businessStreet: { ...businessStreet, error: businessStreetError },
        businessCity: { ...businessCity, error: businessCityError },
        businessState: { ...businessState, error: businessStateError },
        businessZipCode: { ...businessZipCode, error: businessZipCodeError },
      };
    }
  }

  ctx.setIdVerificationInfo({
    ...ctx.state,
    firstName: { ...firstName, error: firstNameError },
    lastName: { ...lastName, error: lastNameError },
    street: { ...street, error: streetError },
    city: { ...city, error: cityError },
    state: { ...state, error: stateError },
    zipCode: { ...zipCode, error: zipCodeError },
    phoneNumber: { ...phoneNumber, error: phoneNumberError },
    monthOfBirth: { ...monthOfBirth, error: monthOfBirthError },
    dayOfBirth: { ...dayOfBirth, error: dayOfBirthError },
    yearOfBirth: { ...yearOfBirth, error: yearOfBirthCommonError },
    fullSocialSecurityNumber: { ...fullSocialSecurityNumber, error: fullSocialSecurityNumberError },
    ...businessFields,
  });

  if (errors.length > 0) {
    return false;
  }
  return true;
}
